import { useQuery } from '@tanstack/react-query';
import { Activity, Database, Layers, RefreshCw } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { POLLING_INTERVALS } from '@/constants/polling';
import { useVisibilityPolling } from '@/hooks/useVisibilityPolling';
import { getAdminHealth, getAdminIncidents, getAdminJobs } from '@/services/api.client';

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      className={`inline-block size-2 rounded-full ${ok ? 'bg-[var(--status-green)]' : 'bg-[var(--status-red)]'}`}
      aria-hidden
    />
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] p-4 flex items-start gap-3">
      <div className="rounded-md bg-primary/10 p-2 mt-0.5">
        <Icon className="size-4 text-primary" />
      </div>
      <div>
        <span className="block text-2xl font-bold text-[var(--text-primary)]">{value}</span>
        <p className="text-xs text-[var(--text-secondary)]">{label}</p>
      </div>
    </div>
  );
}

export function SuperAdminHomePage() {
  const healthInterval = useVisibilityPolling(POLLING_INTERVALS.SUPER_ADMIN.HEALTH);
  const jobsInterval = useVisibilityPolling(POLLING_INTERVALS.SUPER_ADMIN.JOBS);
  const incidentsInterval = useVisibilityPolling(POLLING_INTERVALS.SUPER_ADMIN.INCIDENTS);

  const healthQ = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: getAdminHealth,
    refetchInterval: healthInterval,
  });

  const jobsQ = useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: getAdminJobs,
    refetchInterval: jobsInterval,
  });

  const incidentsQ = useQuery({
    queryKey: ['admin', 'incidents'],
    queryFn: getAdminIncidents,
    refetchInterval: incidentsInterval,
  });

  const health = healthQ.data;
  const healthErrored = healthQ.isError;
  const queues = jobsQ.data ?? [];
  const incidents = incidentsQ.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl text-[var(--text-primary)]">Platform Health</h1>
        <p className="text-sm text-[var(--text-secondary)]">Real-time platform status</p>
      </div>

      {/* Status strip */}
      <div className="flex items-center gap-4 rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] px-4 py-3 text-sm">
        {health ? (
          <>
            <span className="flex items-center gap-2">
              <StatusDot ok={health.database === 'connected'} />
              Database
            </span>
            <span className="flex items-center gap-2">
              <StatusDot ok={health.redis === 'connected'} />
              Redis
            </span>
            <Badge variant={health.status === 'ok' ? 'default' : 'destructive'} className="ml-auto">
              {health.status === 'ok' ? 'Operational' : health.status}
            </Badge>
          </>
        ) : healthErrored ? (
          <span className="flex items-center gap-2 text-[var(--status-red-text)]">
            <StatusDot ok={false} />
            Platform health unavailable — check API connectivity. Queues below still reflect last
            known data.
          </span>
        ) : (
          <Skeleton className="h-4 w-48" />
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          icon={Activity}
          label="Active Users"
          value={
            health ? health.activeUsers : healthErrored ? '—' : <Skeleton className="h-6 w-10" />
          }
        />
        <StatCard
          icon={Layers}
          label="Active Properties"
          value={
            health ? (
              health.activeProperties
            ) : healthErrored ? (
              '—'
            ) : (
              <Skeleton className="h-6 w-10" />
            )
          }
        />
        <StatCard
          icon={Database}
          label="Open Incidents"
          value={incidents ? <span>{incidents.open} open</span> : <Skeleton className="h-6 w-10" />}
        />
        <StatCard
          icon={RefreshCw}
          label="Total Queued Jobs"
          value={
            jobsQ.data !== undefined ? (
              queues.reduce((sum, q) => sum + q.waiting + q.active, 0)
            ) : (
              <Skeleton className="h-6 w-10" />
            )
          }
        />
      </div>

      {/* BullMQ queue table */}
      <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--bg-surface-raised)]">
          <p className="text-sm font-semibold text-[var(--text-primary)]">Job Queues</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bg-surface-raised)] text-[var(--text-secondary)]">
                <th className="text-left px-4 py-2 font-medium">Queue</th>
                <th className="text-right px-4 py-2 font-medium">Waiting</th>
                <th className="text-right px-4 py-2 font-medium">Active</th>
                <th className="text-right px-4 py-2 font-medium">Completed</th>
                <th className="text-right px-4 py-2 font-medium">Failed</th>
              </tr>
            </thead>
            <tbody>
              {jobsQ.isLoading
                ? [1, 2, 3, 4].map((i) => (
                    <tr key={i}>
                      <td className="px-4 py-2" colSpan={5}>
                        <Skeleton className="h-4 w-full" />
                      </td>
                    </tr>
                  ))
                : queues.map((q) => (
                    <tr
                      key={q.name}
                      className="border-b border-[var(--bg-surface-raised)] last:border-0"
                    >
                      <td className="px-4 py-2 font-medium text-[var(--text-primary)]">{q.name}</td>
                      <td className="px-4 py-2 text-right text-[var(--text-secondary)]">
                        {q.waiting}
                      </td>
                      <td className="px-4 py-2 text-right text-[var(--text-secondary)]">
                        {q.active}
                      </td>
                      <td className="px-4 py-2 text-right text-[var(--text-secondary)]">
                        {q.completed}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span
                          className={
                            q.failed > 0
                              ? 'text-[var(--status-red-text)] font-medium'
                              : 'text-[var(--text-secondary)]'
                          }
                        >
                          {q.failed}
                        </span>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
